/**
 * The sources an answer was drawn from.
 *
 * The product rule this implements: an answer grounded in knowledge must
 * visibly show the documents it used, and an answer that is not grounded must
 * not imply that it is. So the "Sources" block is rendered *only* when the
 * server attached citations, and the ungrounded case gets its own explicit,
 * quieter line instead of silence.
 *
 * Citation indices are server-assigned (§4.1) — a model cannot invent one — so
 * the visible `[1]`, `[2]` markers are trustworthy references, not decoration.
 */

import {
  BookOpen,
  FileQuestion,
  FileText,
  GraduationCap,
  Icon,
  type IconComponent,
  Mail,
  Ticket,
} from "@norican/icons";
import { Badge, Card, cn, Typography } from "@norican/react";
import { Link } from "react-router";

import { RelativeTime } from "@/components/common";
import { useT } from "@/i18n";

import {
  asKnowledgeSourceSystem,
  type AssistantCitation,
  type KnowledgeSourceSystem,
} from "./contract";
import { DemoCorpusBadge } from "./demo-corpus";
import { TranslatedText } from "./localized";

const SOURCE_ICONS: Record<KnowledgeSourceSystem, IconComponent> = {
  HELPDESK: BookOpen,
  SHAREPOINT: FileText,
  CLICKLEARN: GraduationCap,
  MICROSOFT: BookOpen,
  MAILBOX: Mail,
  ITSM: Ticket,
  UNKNOWN: FileQuestion,
};

/** The translated name of a source system, tolerating a value this build predates. */
export function useSourceSystemLabel() {
  const { t } = useT("assistant");
  return (value: string): string => t(`knowledge.source.${asKnowledgeSourceSystem(value)}`);
}

export interface SourceBadgeProps {
  sourceSystem: string;
  className?: string;
}

/** The "SharePoint" / "ClickLearn" / "IT mailbox" marker, icon plus name. */
export function SourceBadge({ sourceSystem, className }: SourceBadgeProps) {
  const label = useSourceSystemLabel();
  const system = asKnowledgeSourceSystem(sourceSystem);

  return (
    <Badge tone="neutral" variant="outline" size="sm" className={cn("gap-1", className)}>
      <Icon icon={SOURCE_ICONS[system]} size="xs" />
      {label(system)}
    </Badge>
  );
}

export interface CitationListProps {
  citations: AssistantCitation[];
  className?: string;
}

/**
 * The source list under an answer.
 *
 * Renders nothing at all for an empty list — `UngroundedNote` is the deliberate
 * counterpart, so the two states are never confused with each other.
 */
export function CitationList({ citations, className }: CitationListProps) {
  const { t } = useT("assistant");

  if (citations.length === 0) {
    return null;
  }

  return (
    <section className={cn("flex flex-col gap-2", className)} aria-label={t("citations.title")}>
      <div className="flex items-center gap-2">
        <Icon icon={BookOpen} size="sm" tone="accent" />
        <Typography as="h4" variant="label">
          {t("citations.title")}
        </Typography>
        <Badge tone="accent" variant="subtle" size="xs">
          {t("citations.count", { count: citations.length })}
        </Badge>
      </div>

      <ol className="flex list-none flex-col gap-2 p-0">
        {citations.map((citation) => (
          <li key={`${citation.document_id}:${citation.chunk_id}:${citation.index}`}>
            <Card variant="subtle" padding="sm">
              <div className="flex items-start gap-3">
                <Badge tone="neutral" variant="solid" size="xs" shape="rounded">
                  {citation.index + 1}
                </Badge>

                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <div className="flex flex-wrap items-center gap-2">
                    {citation.url === null || citation.url === undefined ? (
                      <Typography variant="body-sm" className="font-weight-medium">
                        {citation.title}
                      </Typography>
                    ) : citation.url.startsWith("/") ? (
                      // The IT HelpDesk's own knowledge (9 Oct): an in-app page.
                      <Link
                        to={citation.url}
                        className="text-accent-fg-subtle hover:text-accent-hover font-weight-medium text-sm underline underline-offset-2"
                      >
                        {citation.title}
                      </Link>
                    ) : (
                      <a
                        href={citation.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-accent-fg-subtle hover:text-accent-hover font-weight-medium text-sm underline underline-offset-2"
                      >
                        {citation.title}
                        <span className="sr-only"> — {t("citations.open")}</span>
                      </a>
                    )}
                    <SourceBadge sourceSystem={citation.source_system} />
                    {/* Beside the source chip, not under the card: a cropped
                        screenshot of one citation has to carry the warning
                        with it. */}
                    <DemoCorpusBadge />
                  </div>

                  <TranslatedText
                    value={citation.snippet}
                    hideAffordance
                    render={(text) => (
                      <Typography variant="body-sm" tone="muted" className="line-clamp-2">
                        {text}
                      </Typography>
                    )}
                  />

                  {citation.last_modified_at === null ? null : (
                    <Typography variant="caption" tone="muted">
                      {t("citations.updatedLabel")}{" "}
                      <RelativeTime value={citation.last_modified_at} withTime={false} />
                    </Typography>
                  )}
                </div>
              </div>
            </Card>
          </li>
        ))}
      </ol>
    </section>
  );
}

/**
 * The explicit "this answer had no source document" line.
 *
 * Shown instead of the source list so a general-knowledge answer never borrows
 * the authority of a cited one.
 */
export function UngroundedNote({ className }: { className?: string }) {
  const { t } = useT("assistant");
  return (
    <Typography variant="caption" tone="muted" className={className}>
      {t("citations.none")}
    </Typography>
  );
}
